import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';

export interface BookingCartItem {
  cartItemId: string;
  serviceId: string;
  serviceName: string;
  serviceNameEn?: string; // English name for GTM data consistency
  startTime: string;
  endTime: string;
  price: number;
  duration: number;
  quantity: number;
  invitees: string[];
}

interface BookingCartState {
  items: BookingCartItem[];
  addBooking: (item: Omit<BookingCartItem, 'cartItemId'>) => void;
  removeBooking: (cartItemId: string) => void;
  updateBooking: (cartItemId: string, newDetails: Partial<Omit<BookingCartItem, 'cartItemId'>>) => void;
  clearCart: () => void;
}

const filterPastBookings = (items: BookingCartItem[]): BookingCartItem[] => {
  const now = new Date();
  return items.filter((item) => new Date(item.startTime) > now);
};

export const useBookingCartStore = create<BookingCartState>()(
  persist(
    (set) => ({
      items: [],
      addBooking: (item) =>
        set((state) => ({
          items: [...state.items, { ...item, cartItemId: uuidv4(), quantity: 1, invitees: [] }],
        })),
      removeBooking: (cartItemId) =>
        set((state) => ({
          items: state.items.filter((item) => item.cartItemId !== cartItemId),
        })),
      updateBooking: (cartItemId, newDetails) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.cartItemId === cartItemId ? { ...item, ...newDetails, cartItemId } : item
          ),
        }));
      },
      clearCart: () => set({ items: [] }),
    }),
    {
      name: 'booking-cart-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.items = filterPastBookings(state.items);
        }
      },
    }
  )
);
